/* eslint-disable no-nested-ternary */
import { format } from 'date-fns';
import { KeyboardEvent, useRef, useState } from 'react';

import { EncounterSchema } from '../../../services/types/treatment-types';
import { Badge } from '../../ui/catalyst/badge';
import { parseDate } from './utils';

interface TimelineEncounterProps {
  encounter: EncounterSchema & {
    providerId?: string;
    providerName?: string;
  };
  position: number;
  isSelected: boolean;
  onSelect: (encounterId: string) => void;
}

export const TimelineEncounter = ({
  encounter,
  position,
  isSelected,
  onSelect,
}: TimelineEncounterProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    setIsHovered(true);

    // Clear any existing timer
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }

    // Set a small delay before showing tooltip
    tooltipTimerRef.current = setTimeout(() => {
      setShowTooltip(true);
    }, 400);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);

    // Clear any existing timer
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
      tooltipTimerRef.current = null;
    }

    setShowTooltip(false);
  };

  const handleSelect = () => {
    if (encounter.id) {
      onSelect(encounter.id);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      handleSelect();
    }
  };

  const dateObj = encounter.date ? parseDate(encounter.date) : null;
  const formattedDate = dateObj ? format(dateObj, 'MMM d, yyyy') : 'Undated';

  const shortSummary = encounter.description
    ? encounter.description.length > 30
      ? `${encounter.description.substring(0, 30)}...`
      : encounter.description
    : 'Encounter';

  const primaryService =
    encounter.services && encounter.services.length > 0
      ? encounter.services[0]
      : null;

  const displayLabel = primaryService || shortSummary;

  // Determine tooltip position - if near the edge of the timeline, invert
  const tooltipPosition =
    position < 20 ? 'left' : position > 80 ? 'right' : 'center';

  return (
    <div
      className="absolute transform -translate-x-1/2 cursor-pointer transition-all duration-200 z-[5] hover:z-10"
      style={{
        left: `${position}%`,
        minWidth: '60px',
        maxWidth: '120px',
        top: '50%',
        transform: `translate(-50%, -50%) ${isHovered || isSelected ? 'scale(1.05)' : 'scale(1)'}`,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease, z-index 0.1s',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      aria-label={`Encounter on ${formattedDate}: ${shortSummary}`}
      role="button"
    >
      <div
        className={`px-2 py-1.5 rounded-md text-xs font-medium shadow-sm 
          overflow-hidden text-ellipsis whitespace-nowrap transition-all duration-150
          ${
            isSelected
              ? 'bg-blue-500 border-blue-600 text-white shadow-md ring-2 ring-blue-300 ring-opacity-50'
              : isHovered
                ? 'bg-gray-100 border-gray-200 text-gray-800 shadow'
                : 'bg-white border border-gray-200 text-gray-700'
          }`}
        title={`${formattedDate}: ${encounter.description}`}
      >
        {displayLabel}
      </div>

      {/* Tooltip with dynamic positioning - only shown when specifically hovering on this encounter */}
      {showTooltip && (
        <div
          className={`fixed z-[1000] bg-white shadow-xl border border-gray-100 rounded-md p-3 w-64 -mt-2
            backdrop-blur-sm bg-white/95
            ${
              tooltipPosition === 'center'
                ? 'left-1/2 transform -translate-x-1/2'
                : tooltipPosition === 'left'
                  ? 'left-0'
                  : 'right-0'
            }`}
          style={{
            bottom: '100%',
            marginBottom: '10px',
            animation: 'tooltipFadeIn 0.2s ease-out forwards',
          }}
        >
          <div className="space-y-1.5">
            <div className="text-xs font-semibold text-gray-800 border-b border-gray-50 pb-1">
              {formattedDate}
            </div>
            <p className="text-xs text-gray-700 leading-snug">
              {encounter.description}
            </p>
            {encounter.providerName && (
              <div className="text-[11px] text-gray-500 pt-1 border-t border-gray-50 mt-1.5">
                Provider:{' '}
                <span className="font-medium text-gray-600">
                  {encounter.providerName}
                </span>
              </div>
            )}
            {encounter.services && encounter.services.length > 0 && (
              <div className="pt-1 border-t border-gray-50 mt-1.5">
                <div className="text-[11px] text-gray-500 mb-0.5">
                  Services:
                </div>
                <div className="flex flex-wrap gap-1">
                  {encounter.services.map((service, idx) => (
                    <Badge
                      key={idx}
                      color="blue"
                      className="text-[10px] px-1.5 py-0.5 font-normal bg-blue-50 text-blue-600 border border-blue-100"
                    >
                      {service}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
          {/* Dynamic tooltip arrow positioning */}
          <div
            className={`absolute bottom-0 transform translate-y-full w-3 h-3
              ${
                tooltipPosition === 'center'
                  ? 'left-1/2 -translate-x-1/2'
                  : tooltipPosition === 'left'
                    ? 'left-4'
                    : 'right-4'
              }`}
          >
            <div className="w-full h-full bg-white border-r border-b border-gray-100 rotate-45 transform origin-top-left" />
          </div>
        </div>
      )}

      {/* Selected indicator dot */}
      {isSelected && (
        <div className="absolute h-2 w-2 bg-blue-500 rounded-full -bottom-2 left-1/2 transform -translate-x-1/2 shadow-sm animate-pulse" />
      )}
    </div>
  );
};
